import styled from 'styled-components';
import { DataTypeType, GenericDataType } from 'modules/domains';
import { pxToRem } from 'utils/convert';
import { isDefined, isUndefined } from 'utils/tsHelpers';
import { InputText } from 'ui/inputs/inputText/InputText';
import { Search } from 'ui/icons/action/Search';
import { BasicSelectWithNestedChildren, SelectWrapper, SelectValueType } from 'ui/selects';

import { useIsUserOption } from './hooks/useIsUserOption';
import { Trigger } from '../conditionValue';
import { useSearchFieldContext } from '../context/SearchFieldContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { useConditionFieldNameErrors } from '../../../hooks/form';
import { allEvents } from '../../../constants';
import { FieldSearchOption } from '../../../types';
import { useEventOptions } from './hooks/useEventOptions';

const FIELD_SELECT_WIDTH = pxToRem(350);

const StyledBasicSelect = styled(BasicSelectWithNestedChildren)`
  ${({ theme }) => theme.styles.body.body2};
  width: fit-content;
  ${SelectWrapper} {
    border: none;
    background: none;
    :hover {
      border-color: none;
    }
  }
`;

const createGetEvent = (events: DataTypeType[]) => (values: SelectValueType[]) => {
  const options = events.filter((event) => event === values[0]);
  const event = options[0];
  return event;
};

const getDefaultValues = (dataType?: DataTypeType) => {
  if (isUndefined(dataType) || dataType === GenericDataType.User) return [''];
  else return [dataType];
};

export const DATA_TEST = {
  select: 'event-select',
};

export const EventSelect = () => {
  const { t } = useTranslation();
  const { dataType, changeDataType, changeOption } = useSearchFieldContext();
  const isUserOption = useIsUserOption();
  const errors = useConditionFieldNameErrors();
  // Error only occurs if option isn't selected upon submitting form
  const hasError = errors && !dataType;

  const getEvent = createGetEvent(allEvents);
  const options = useEventOptions();
  const placeholder = t('selector.option.event');

  if (isUserOption) return null;

  return (
    <StyledBasicSelect
      data-test={DATA_TEST.select}
      isError={hasError}
      options={options}
      placeholder={placeholder}
      Trigger={Trigger}
      width={FIELD_SELECT_WIDTH}
      defaultValues={getDefaultValues(dataType)}
      onSelect={(option) => {
        const event = getEvent(option);
        if (isDefined(event)) {
          changeDataType(event);
          changeOption(FieldSearchOption.Event);
        }
      }}
    >
      <InputText
        type="search"
        autoComplete="off"
        id="event-search"
        Prefix={<Search />}
        placeholder={placeholder}
        value=""
        autoFocus
      />
    </StyledBasicSelect>
  );
};
